/**
 * Dual billing rail: Shopify App Billing vs direct (Dodo/Stripe/etc.).
 * App Store policy: merchants who installed via Shopify must use Shopify Billing API.
 */

import { auth, db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { userService } from '../services/firestore';
import type { ShopifyBillingSource } from '../types/shopifyInstallWizard';

/** `shop` is present when the embedded app loads from Shopify Admin (and typical OAuth return URLs). */
export function readShopifyShopQueryParam(search: string): string | null {
  try {
    const shop = new URLSearchParams(search).get('shop');
    const trimmed = shop?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

export interface PreferShopifyBillingOptions {
  locationSearch: string;
  isShopifyEmbedded?: boolean;
  billingSource?: ShopifyBillingSource | null;
  acquisitionChannel?: string | null;
  hasShopifyIntegration?: boolean;
}

/**
 * Use Shopify Billing when:
 * - URL has ?shop= (Admin / OAuth), or
 * - running inside Shopify iframe, or
 * - account was created via Shopify App Store install (billingSource / acquisitionChannel), or
 * - current workspace has a connected Shopify integration.
 */
export function preferShopifyAppBilling(options: PreferShopifyBillingOptions): boolean {
  const { locationSearch, isShopifyEmbedded, billingSource, acquisitionChannel, hasShopifyIntegration } =
    options;
  if (billingSource === 'shopify') return true;
  if (acquisitionChannel === 'shopify_app_store') return true;
  if (isShopifyEmbedded) return true;
  if (hasShopifyIntegration) return true;
  return !!readShopifyShopQueryParam(locationSearch);
}

/** @deprecated Use preferShopifyAppBilling({ locationSearch }) */
export function preferShopifyAppBillingFromSearch(locationSearch: string): boolean {
  return preferShopifyAppBilling({ locationSearch });
}

const OFF_PLATFORM_BILLING_BLOCKED_MSG =
  'This account is billed through Shopify. Open Scubamail from Shopify Admin to upgrade or change your plan.';

export function offPlatformBillingBlockedMessage(): string {
  return OFF_PLATFORM_BILLING_BLOCKED_MSG;
}

function isShopifyIntegrationRow(row: { slug?: string; integrationId?: string }): boolean {
  return row.slug === 'shopify' || row.integrationId === 'shopify';
}

async function workspaceHasShopifyIntegration(userId: string, workspaceId: string): Promise<boolean> {
  const integrationsRef = collection(db, 'userIntegrations');
  const q = query(
    integrationsRef,
    where('userId', '==', userId),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'connected')
  );
  const snap = await getDocs(q);
  return snap.docs.some((docSnap) => isShopifyIntegrationRow(docSnap.data() as { slug?: string; integrationId?: string }));
}

async function userHasAnyShopifyIntegration(userId: string): Promise<boolean> {
  const integrationsRef = collection(db, 'userIntegrations');
  const q = query(integrationsRef, where('userId', '==', userId), where('status', '==', 'connected'));
  const snap = await getDocs(q);
  return snap.docs.some((docSnap) => isShopifyIntegrationRow(docSnap.data() as { slug?: string; integrationId?: string }));
}

/**
 * Returns true when Dodo/Stripe checkout must not be offered (Shopify billing only).
 */
export async function shouldBlockOffPlatformBilling(workspaceId?: string | null): Promise<boolean> {
  if (typeof window !== 'undefined') {
    const inIframe = window.self !== window.top;
    if (inIframe) return true;
    if (readShopifyShopQueryParam(window.location.search)) return true;
  }

  const user = auth.currentUser;
  if (!user) return false;

  try {
    const profile = await userService.getUserProfile(user.uid);
    if (profile?.billingSource === 'shopify') return true;
    if (profile?.acquisitionChannel === 'shopify_app_store') return true;

    if (workspaceId) {
      return await workspaceHasShopifyIntegration(user.uid, workspaceId);
    }
    return await userHasAnyShopifyIntegration(user.uid);
  } catch (e) {
    console.warn('shouldBlockOffPlatformBilling: profile/workspace check failed', e);
  }

  return false;
}
